import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MapPin,
  CalendarDays,
  ArrowRight,
  Droplets,
  Network,
  Search,
  Heart,
  Eye,
  MessageCircle,
  Play,
  Mic,
  UserPlus,
  ChevronRight,
  Clock,
  Users,
  Gift,
  LayoutDashboard,
} from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import PageLayout from '../components/layout/PageLayout';
import { api } from '../services/api';

const NAV_LINKS = [
  { label: 'Sobre Nós', to: '/sobre' },
  { label: 'Pregações', to: '/pregações' },
  { label: 'Células', to: '/celulas' },
  { label: 'Eventos', to: '/evento' },
  { label: 'Galeria', to: '/galeria' },
  { label: 'Contato', to: '/contato' },
];

const ICONS = {
  map: MapPin,
  network: Network,
  users: Users,
  calendar: CalendarDays,
  heart: Heart,
  play: Play,
  mic: Mic,
  userPlus: UserPlus,
  gift: Gift,
};

// ----------------------------- HERO -----------------------------
function HeroSection({ content }) {
  const [query, setQuery] = useState('');
  const c = content || {};
  const tags = c.popularTags || [];

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={c.heroImage} alt="Comunidade Águas Vivas" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/85 via-primary-dark/70 to-primary-dark/90" />
      </div>

      <div className="app-container relative py-16 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          {c.badge && (
            <Badge tone="accent" className="mb-4">
              <Droplets size={12} /> {c.badge}
            </Badge>
          )}
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-white md:text-5xl">
            {c.title}
            {c.highlight && (
              <>
                <br />
                <span className="text-accent">{c.highlight}</span>
              </>
            )}
          </h1>
          {c.subtitle && (
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
              {c.subtitle}
            </p>
          )}

          <div className="mx-auto mt-8 max-w-xl">
            <div className="flex items-center gap-2 rounded-full bg-white p-1.5 shadow-lift">
              <Search size={18} className="ml-3 shrink-0 text-ink-faint" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={c.searchPlaceholder || 'Buscar...'}
                className="w-full bg-transparent py-2 text-sm text-ink outline-none placeholder:text-ink-faint"
              />
              <button className="shrink-0 rounded-full bg-primary px-5 py-2.5 font-heading text-sm font-bold text-white transition-colors hover:bg-primary-dark">
                Buscar
              </button>
            </div>
            {tags.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                <span className="text-xs font-medium text-white/70">Populares:</span>
                {tags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setQuery(tag.replace('#', ''))}
                    className="rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-white/20"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ----------------------------- STATS -----------------------------
function StatsCards({ cards, nextEvent }) {
  return (
    <div className="app-container -mt-8 relative z-10">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map((card) => {
          const Icon = ICONS[card.icon] || MapPin;
          return (
            <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft transition-transform duration-200 hover:-translate-y-0.5">
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${card.tone || 'bg-primary-light text-primary'}`}>
                <Icon size={18} />
              </span>
              <p className="mt-3 font-heading text-3xl font-extrabold text-ink">{card.value}</p>
              <p className="text-xs font-medium text-ink-soft">{card.label}</p>
            </div>
          );
        })}
      </div>

      {nextEvent && (
        <Link
          to="/evento"
          data-testid="home-next-event"
          className="mt-4 block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift"
        >
          <div className="flex items-center gap-4 p-4 md:p-5">
            <img src={nextEvent.cover} alt="" className="h-16 w-16 rounded-xl object-cover md:h-20 md:w-20" />
            <div className="min-w-0 flex-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-accent-dark">
                Próximo evento
              </span>
              <p className="truncate font-heading font-bold text-ink">{nextEvent.title}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-ink-soft">
                <Clock size={13} />
                {format(parseISO(nextEvent.date), "dd 'de' MMM · HH:mm", { locale: ptBR })}
                <span className="text-ink-faint">·</span>
                <MapPin size={13} className="text-ink-faint" />
                <span className="truncate">{nextEvent.location}</span>
              </p>
            </div>
            <ArrowRight size={18} className="shrink-0 text-ink-faint" />
          </div>
        </Link>
      )}
    </div>
  );
}

// ----------------------------- MEDIA FEED -----------------------------
function MediaFeed({ title, subtitle, limit }) {
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.getGallery().then(setGallery);
  }, []);

  const items = gallery.slice(0, limit || 6);

  return (
    <section className="app-container mt-10">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-heading text-xl font-extrabold text-ink md:text-2xl">{title || 'Momentos da Comunidade'}</h2>
          {subtitle && <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>}
        </div>
        <Link to="/galeria" className="flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary-dark">
          Ver tudo <ArrowRight size={15} />
        </Link>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {items.map((item) => (
          <Link
            key={item.id}
            to="/galeria"
            className="group relative aspect-square overflow-hidden rounded-2xl"
          >
            <img
              src={item.img}
              alt={item.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 via-black/10 to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <div>
                <Badge tone="accent" className="mb-1 text-[10px]">{item.category}</Badge>
                <p className="truncate text-xs font-semibold text-white">{item.title}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ----------------------------- RECENT POSTS -----------------------------
function RecentPosts({ title, subtitle, posts }) {
  const list = posts || [];
  return (
    <section className="app-container mt-12">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-heading text-xl font-extrabold text-ink md:text-2xl">{title || 'Últimas Atividades'}</h2>
          {subtitle && <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>}
        </div>
        <Link to="/pregações" className="flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary-dark">
          Ver todos <ArrowRight size={15} />
        </Link>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {list.map((post) => (
          <article
            key={post.id}
            className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
          >
            <div className="relative aspect-[16/10] overflow-hidden">
              <img
                src={post.cover}
                alt={post.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <Badge tone="primary" className="absolute left-3 top-3">{post.category}</Badge>
            </div>
            <div className="p-4">
              <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-ink transition-colors group-hover:text-primary">
                {post.title}
              </h3>
              <p className="mt-2 text-xs text-ink-soft">
                {post.author} · {format(parseISO(post.date), "dd 'de' MMM", { locale: ptBR })}
              </p>
              <div className="mt-3 flex items-center gap-4 border-t border-slate-100 pt-3 text-xs text-ink-faint">
                <span className="flex items-center gap-1"><Eye size={13} /> {post.views.toLocaleString('pt-BR')}</span>
                <span className="flex items-center gap-1"><Heart size={13} /> {post.likes}</span>
                <span className="flex items-center gap-1"><MessageCircle size={13} /> {post.comments}</span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// ----------------------------- MODULES -----------------------------
function ModulesSection({ title, subtitle, modules }) {
  const list = modules || [];
  return (
    <section className="mt-12 bg-white py-12">
      <div className="app-container">
        <div className="text-center">
          <h2 className="font-heading text-xl font-extrabold text-ink md:text-2xl">{title || 'Conteúdos por Categoria'}</h2>
          {subtitle && <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>}
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {list.map((mod) => (
            <div key={mod.day} className="overflow-hidden rounded-2xl border border-slate-200 bg-canvas">
              <div className="flex items-center justify-between bg-primary px-5 py-3">
                <div>
                  <p className="font-heading text-sm font-bold text-white">{mod.day}</p>
                  <p className="text-xs text-white/80">{mod.title}</p>
                </div>
                <Badge tone="accent">{mod.time}</Badge>
              </div>

              <div className="p-4">
                <div className="group relative aspect-[16/9] overflow-hidden rounded-xl">
                  <img
                    src={mod.highlight.cover}
                    alt={mod.highlight.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <p className="text-sm font-bold leading-snug text-white">{mod.highlight.title}</p>
                    <p className="mt-0.5 line-clamp-1 text-xs text-white/80">{mod.highlight.desc}</p>
                  </div>
                </div>

                <ul className="mt-4 space-y-1">
                  {(mod.links || []).map((link) => (
                    <li key={link}>
                      <Link
                        to="/pregações"
                        className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-ink-soft transition-colors hover:bg-primary-light hover:text-primary"
                      >
                        <ChevronRight size={14} className="text-primary" />
                        {link}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----------------------------- RESOURCES -----------------------------
function ResourcesGrid({ title, subtitle, resources }) {
  const list = resources || [];
  return (
    <section className="app-container mt-12">
      <div className="text-center">
        <h2 className="font-heading text-xl font-extrabold text-ink md:text-2xl">{title || 'Recursos Rápidos'}</h2>
        {subtitle && <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>}
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-3">
        {list.map((res) => {
          const Icon = ICONS[res.icon] || Heart;
          return (
            <Link
              key={res.title}
              to={res.to}
              className="group flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${res.tone}`}>
                <Icon size={26} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-ink">{res.title}</h3>
              <p className="mt-1 text-sm text-ink-soft">{res.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary-light px-4 py-2 font-heading text-xs font-bold text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                {res.cta} <ArrowRight size={13} />
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ----------------------------- BANNERS -----------------------------
function ConversionBanners({ banners }) {
  const list = banners || [];
  return (
    <section className="app-container mt-12">
      <div className="grid gap-4 md:grid-cols-2">
        {list.map((b) => {
          const Icon = ICONS[b.icon] || UserPlus;
          return (
            <div key={b.title} className={`relative overflow-hidden rounded-2xl ${b.tone || 'bg-primary-dark'} p-6 text-white shadow-lift md:p-8`}>
              <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10" />
              <div className="absolute -bottom-10 -left-6 h-32 w-32 rounded-full bg-accent/20" />
              <div className="relative">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15">
                  <Icon size={24} />
                </span>
                <h3 className="mt-4 font-heading text-xl font-extrabold md:text-2xl">{b.title}</h3>
                <p className="mt-2 max-w-sm text-sm text-white/80">{b.desc}</p>
                <Link
                  to={b.to}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-heading text-sm font-bold text-primary transition-transform duration-200 hover:-translate-y-0.5"
                >
                  {b.cta} <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ----------------------------- EMPTY STATE -----------------------------
function EmptyHome() {
  return (
    <div className="app-container flex flex-col items-center justify-center py-24 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-light text-primary">
        <LayoutDashboard size={28} />
      </span>
      <h2 className="mt-4 font-heading text-xl font-extrabold text-ink">Conteúdo em breve</h2>
      <p className="mt-1 max-w-sm text-sm text-ink-soft">
        A página inicial ainda não foi configurada. Em breve você encontrará novidades da comunidade.
      </p>
    </div>
  );
}

// ----------------------------- RENDERER -----------------------------
function SectionRenderer({ section, stats, nextEvent }) {
  const c = section.content || {};
  switch (section.type) {
    case 'hero':
      return <HeroSection content={c} />;
    case 'stats':
      return <StatsCards cards={c.cards || []} nextEvent={nextEvent} />;
    case 'nextEvent':
      return nextEvent ? (
        <div className="app-container mt-10">
          <Link
            to="/evento"
            data-testid="home-next-event"
            className="block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift"
          >
            <div className="flex items-center gap-4 p-4 md:p-5">
              <img src={nextEvent.cover} alt="" className="h-16 w-16 rounded-xl object-cover md:h-20 md:w-20" />
              <div className="min-w-0 flex-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-accent-dark">
                  Próximo evento
                </span>
                <p className="truncate font-heading font-bold text-ink">{nextEvent.title}</p>
                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-ink-soft">
                  <Clock size={13} />
                  {format(parseISO(nextEvent.date), "dd 'de' MMM · HH:mm", { locale: ptBR })}
                  <span className="text-ink-faint">·</span>
                  <MapPin size={13} className="text-ink-faint" />
                  <span className="truncate">{nextEvent.location}</span>
                </p>
              </div>
              <ArrowRight size={18} className="shrink-0 text-ink-faint" />
            </div>
          </Link>
        </div>
      ) : null;
    case 'mediaFeed':
      return <MediaFeed title={section.title} subtitle={section.subtitle} limit={c.limit} />;
    case 'recentPosts':
      return <RecentPosts title={section.title} subtitle={section.subtitle} posts={c.posts} />;
    case 'modules':
      return <ModulesSection title={section.title} subtitle={section.subtitle} modules={c.modules} />;
    case 'resources':
      return <ResourcesGrid title={section.title} subtitle={section.subtitle} resources={c.resources} />;
    case 'banners':
      return <ConversionBanners banners={c.banners} />;
    default:
      return null;
  }
}

// ----------------------------- HOME -----------------------------
export default function Home() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ cells: 0, networks: 0 });
  const [nextEvent, setNextEvent] = useState(null);

  useEffect(() => {
    Promise.all([api.getHomeSections(), api.getCells(), api.getNetworks(), api.getEvents()]).then(
      ([homeSections, cells, networks, events]) => {
        setSections(homeSections);
        setStats({ cells: cells.length, networks: networks.length });
        const upcoming = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));
        setNextEvent(upcoming[0]);
        setLoading(false);
      }
    );
  }, []);

  // Resolve dynamic values for stats cards (e.g. value: 'cells' -> stats.cells)
  const resolvedSections = sections.map((s) => {
    if (s.type === 'stats') {
      const cards = (s.content?.cards || []).map((card) => {
        if (card.value === 'cells') return { ...card, value: stats.cells };
        if (card.value === 'networks') return { ...card, value: stats.networks };
        return card;
      });
      return { ...s, content: { ...s.content, cards } };
    }
    return s;
  });

  return (
    <PageLayout
      navLinks={NAV_LINKS}
      footerColumns={[
        {
          title: 'Navegação',
          links: [
            ['Em que Cremos', '/sobre'],
            ['História', '/sobre'],
            ['Eventos', '/evento'],
            ['Células', '/celulas'],
            ['Galeria', '/galeria'],
          ],
        },
        {
          title: 'Institucional',
          links: [
            ['Política de Privacidade', '/privacidade'],
            ['Relatórios', '/relatorios'],
            ['Doações', '/contato'],
            ['Área Administrativa', '/admin'],
          ],
        },
      ]}
    >
      {loading ? (
        <div className="app-container py-24 text-center text-sm text-ink-soft">Carregando...</div>
      ) : resolvedSections.length === 0 ? (
        <EmptyHome />
      ) : (
        resolvedSections.map((section) => (
          <SectionRenderer key={section.id} section={section} stats={stats} nextEvent={nextEvent} />
        ))
      )}
    </PageLayout>
  );
}
